/**
 * Pezzi del telefono per la scheda di un progetto (7/10/2026), usati dalla
 * Panoramica (ProgettoMobile) e dalle sezioni (ProgettoSezioniMobile).
 *
 * Sono i pezzi che il kit (components/mobile) non ha ancora: la riga di un
 * task con la spunta, la riga-comando in fondo a un gruppo, l'intestazione di
 * una sezione, il pannello per un task al volo e quello di un messaggio del
 * Diario. Stesse chiamate della scheda del computer.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { MRow } from '../../components/mobile/MobileKit';
import type { ProjectFull } from './ProjectDetailPage';
import { cosaE, giorniA, nomeCliente } from './ProgettiMobile';
import { incollaPiuRighe } from '../tasks/tasksApi';
import { chiHaScritto } from '../assistente/registroApi';

export type TaskProgetto = ProjectFull['tasks'][number];
export type Comunicazione = ProjectFull['communications'][number];

export function apriTaskModale(id: string) {
  useModalStore.getState().open('taskDetail', { taskId: id });
}

/** "6 ott", "6 ott 2025" se non è quest'anno. */
export function giornoBreve(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const stessoAnno = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', ...(stessoAnno ? {} : { year: 'numeric' }) }).replace('.', '');
}

/** "A. Anfuso": nella riga basta questo, il nome intero è nella scheda del task. */
export const personaBreve = (u: { firstName?: string; lastName?: string } | null | undefined) =>
  u?.firstName ? `${u.firstName[0]}. ${u.lastName ?? ''}`.trim() : null;

// ── La riga di un task, con la spunta ─────────────────────────────────────

/**
 * Chiudere un task è un gesto da telefono (mobile.md §0): il cerchio a
 * sinistra lo spunta, il resto della riga apre la scheda del task. Il rosso
 * sta in un posto solo, nella riga sotto (scaduto o bloccato da noi).
 */
export function RigaTask({ t, senzaStato }: { t: TaskProgetto; /** Il gruppo dice già lo stato ("In corso"): non si ripete. */ senzaStato?: boolean }) {
  const qc = useQueryClient();
  const fatto = t.status === 'DONE';
  const spunta = useMutation({
    mutationFn: () => api.patch(`/tasks/${t.id}`, { status: fatto ? 'TODO' : 'DONE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project'] }),
    onError: (e: any) => toast.alert('Task non aggiornato', e?.message ?? 'Riprova'),
  });
  const scaduto = !fatto && !!t.dueDate && giorniA(t.dueDate.slice(0, 10) + 'T00:00:00Z') < 0;
  const pezzi: ReactNode[] = [];
  if (t.status === 'BLOCKED') {
    pezzi.push(t.blockedBy === 'CLIENTE'
      ? <span key="s" className="is-warn">Aspettiamo il cliente</span>
      : <span key="s" className="is-alert">Bloccato{t.blockedReason ? `: ${t.blockedReason}` : ''}</span>);
  } else if (t.status === 'IN_PROGRESS' && !senzaStato) pezzi.push('In corso');
  else if (t.status === 'REVIEW' && !senzaStato) pezzi.push('In revisione');
  else if (fatto) pezzi.push(t.completedAt ? `Fatto il ${giornoBreve(t.completedAt)}` : senzaStato ? null : 'Fatto');
  if (t.dueDate && !fatto) {
    pezzi.push(scaduto && t.status !== 'BLOCKED'
      ? <span key="d" className="is-alert">scaduto il {giornoBreve(t.dueDate)}</span>
      : `entro il ${giornoBreve(t.dueDate)}`);
  }
  const chi = personaBreve(t.assignee);
  if (chi) pezzi.push(chi);
  if (t.priority === 'ALTA' && !fatto) pezzi.push('priorità alta');
  const riga = pezzi.filter((x) => x != null && x !== '');
  return (
    <div className="pjm-check-row">
      <button
        type="button"
        className="pjm-check"
        role="checkbox"
        aria-checked={fatto}
        aria-label={fatto ? `Riapri "${t.title}"` : `Segna "${t.title}" come fatto`}
        disabled={spunta.isPending}
        onClick={() => spunta.mutate()}
      >
        <span>{fatto && <Icon name="check" size={13} strokeWidth={2.5} />}</span>
      </button>
      <MRow
        title={fatto ? <span className="pjm-barrato">{t.title}</span> : t.title}
        sub={riga.length ? riga.map((x, i) => <span key={i}>{i > 0 && ' · '}{x}</span>) : undefined}
        onClick={() => apriTaskModale(t.id)}
      />
    </div>
  );
}

/** I task aperti nell'ordine in cui servono: scaduti, bloccati, poi per scadenza. */
export function ordinaTask(a: TaskProgetto, b: TaskProgetto): number {
  const peso = (t: TaskProgetto) => {
    if (t.status === 'DONE') return 4;
    if (t.dueDate && giorniA(t.dueDate.slice(0, 10) + 'T00:00:00Z') < 0) return 0;
    if (t.status === 'BLOCKED') return 1;
    if (t.status === 'IN_PROGRESS') return 2;
    return 3;
  };
  return peso(a) - peso(b) || (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999');
}

// ── Riga-comando in fondo a un gruppo ─────────────────────────────────────

/** "Carica dal preventivo", "Aggiungi una milestone": una riga, non un bottone che galleggia. */
export function RigaAzione({ icon, label, onClick, danger }: { icon: IconName; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" className={`pjm-azione${danger ? ' is-danger' : ''}`} onClick={onClick}>
      <Icon name={icon} size={15} />
      <span>{label}</span>
    </button>
  );
}

// ── Intestazione di una sezione ───────────────────────────────────────────

/** Il progetto piccolo sopra, il nome della sezione grande: si sa sempre dove si è. */
export function TitoloSezione({ p, titolo, sotto }: { p: ProjectFull; titolo: string; sotto?: ReactNode }) {
  return (
    <div className="pjm-sez-hd">
      <div className="pjm-meta pjm-una-riga">{nomeCliente(p.client)} · {cosaE(p.name)}</div>
      <h1 className="pjm-hd-title">{titolo}</h1>
      {sotto && <div className="pjm-meta">{sotto}</div>}
    </div>
  );
}

// ── Un task al volo ───────────────────────────────────────────────────────

/**
 * "Nuovo task" dal telefono: un campo e un tasto, come l'aggiunta veloce del
 * computer (stessa chiamata: entra fra i task da fare, priorità media). Per
 * assegnatario e scadenza c'è la scheda completa, un tocco più in là.
 */
export function NuovoTaskSheet({ projectId, open, onClose }: { projectId: string; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [testo, setTesto] = useState('');
  useEffect(() => { if (open) setTesto(''); }, [open]);
  const aggiungi = useMutation({
    mutationFn: (title: string) => api.post('/tasks', { projectId, title, status: 'TODO', priority: 'MEDIA' }),
    onSuccess: (_r, title) => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      toast.ok('Task aggiunto', title);
      onClose();
    },
    onError: (e: any) => toast.alert('Task non creato', e?.message ?? 'Riprova'),
  });
  const salva = () => { const t = testo.trim(); if (t && !aggiungi.isPending) aggiungi.mutate(t); };
  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      title="Nuovo task"
      footer={<button className="btn btn-primary m-block" disabled={!testo.trim() || aggiungi.isPending} onClick={salva}>{aggiungi.isPending ? 'Aggiungo…' : 'Aggiungi il task'}</button>}
    >
      <div className="pjm-sheet">
        <input
          className="inp"
          autoFocus
          enterKeyHint="done"
          placeholder="Cosa c'è da fare?"
          aria-label="Titolo del task"
          value={testo}
          onChange={(e) => setTesto(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); salva(); } }}
          // Più righe incollate: la scheda completa con l'anteprima, invece di un task col testo unito.
          onPaste={(e) => {
            const aperta = incollaPiuRighe(e, testo, (t) => {
              onClose();
              useModalStore.getState().open('newTask', { projectId, prefill: { testo: t } });
            });
            if (aperta) setTesto('');
          }}
        />
        <RigaAzione
          icon="calendar"
          label="Con assegnatario e scadenza"
          onClick={() => { onClose(); useModalStore.getState().open('newTask', { projectId, prefill: testo.trim() ? { title: testo.trim() } : undefined }); }}
        />
      </div>
    </MobileSheet>
  );
}

// ── Un messaggio del Diario ───────────────────────────────────────────────

const TIPO_COMM: Record<string, { label: string; icon: IconName }> = {
  EMAIL: { label: 'Email', icon: 'mail' },
  CALL: { label: 'Chiamata', icon: 'message' },
  MEETING: { label: 'Incontro', icon: 'calendar' },
  NOTE: { label: 'Nota', icon: 'pencil' },
};
export const tipoComm = (k: string) => TIPO_COMM[k] ?? { label: k.charAt(0) + k.slice(1).toLowerCase(), icon: 'pin' as IconName };

/** Interne: le note e quello che è segnato "interno". Le altre sono con il cliente (stessa regola del computer). */
export const eInterna = (c: Comunicazione) => c.tag === 'interno' || c.kind === 'NOTE';

export function RigaComunicazione({ c, onApri }: { c: Comunicazione; onApri: () => void }) {
  const tipo = tipoComm(c.kind);
  // Scritta da MUNE (29/10/2026, 2D): «Andrea Anfuso, tramite MUNE», come nel Log attività.
  const chi = c.author ? chiHaScritto(`${c.author.firstName} ${c.author.lastName}`, c.aiActionId) : c.fromText;
  return (
    <MRow
      icon={tipo.icon}
      title={c.subject}
      sub={[giornoBreve(c.occurredAt), c.toText ? `a ${c.toText}` : chi, c.tag && c.tag !== 'interno' ? c.tag : null].filter(Boolean).join(' · ')}
      onClick={onApri}
    />
  );
}

/**
 * Il messaggio intero, dal basso, come in un'app di posta: chi, quando, il
 * testo, e sotto la risposta (che resta nel Diario come nota) o un task.
 */
export function ComunicazioneSheet({ projectId, c, onClose }: { projectId: string; c: Comunicazione | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [risposta, setRisposta] = useState('');
  useEffect(() => { setRisposta(''); }, [c?.id]);
  const invia = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post(`/projects/${projectId}/communications`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      setRisposta('');
      toast.ok('Risposta salvata nel Diario');
      onClose();
    },
    onError: (e: any) => toast.alert('Invio fallito', e?.message ?? 'Riprova'),
  });
  const tipo = c ? tipoComm(c.kind) : null;
  return (
    <MobileSheet open={!!c} onClose={onClose} title={c?.subject}>
      {c && tipo && (
        <div className="pjm-sheet">
          <div className="pjm-meta">
            {tipo.label} · {new Date(c.occurredAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
            {c.durationMin ? ` · ${c.durationMin} min` : ''}
            {c.author ? ` · ${chiHaScritto(`${c.author.firstName} ${c.author.lastName}`, c.aiActionId)}` : c.fromText ? ` · ${c.fromText}` : ''}
            {c.toText ? ` → ${c.toText}` : ''}
          </div>
          <div className={`pjm-testo${c.body ? '' : ' is-mute'}`}>{c.body || 'Nessun testo.'}</div>
          <textarea
            className="inp"
            rows={3}
            placeholder="Rispondi o aggiungi una nota…"
            aria-label="Risposta"
            value={risposta}
            onChange={(e) => setRisposta(e.target.value)}
          />
          <div className="pjm-azioni">
            <button
              className="btn"
              onClick={() => { onClose(); useModalStore.getState().open('newTask', { projectId, prefill: { title: `Follow-up: ${c.subject}`, description: c.body ?? '' } }); }}
            >
              <Icon name="plus" size={12} /> Crea un task
            </button>
            <button
              className="btn btn-primary"
              disabled={!risposta.trim() || invia.isPending}
              onClick={() => invia.mutate({ kind: 'NOTE', subject: `Re: ${c.subject}`, body: risposta, occurredAt: new Date().toISOString(), tag: c.tag })}
            >
              <Icon name="send" size={12} /> {invia.isPending ? 'Invio…' : 'Salva la risposta'}
            </button>
          </div>
        </div>
      )}
    </MobileSheet>
  );
}
